import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../component/Navbar';
import './ClientRefById.css';

const API_BASE_URL = 'https://apiv2.labridge.in';

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || error.message || fallback;

const getClients = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

export const searchClientsByEmail = (email) =>
  axios.get(`${API_BASE_URL}/admin/clients/search-by-email`, { params: { email } });

export const updateClientRefById = ({ partnerId, refbyId }) =>
  axios.post(`${API_BASE_URL}/admin/clients/update-refbyid`, { partnerId, refbyId });

function ClientRefById() {
  const navigate = useNavigate();
  const [emailQuery, setEmailQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [refbyId, setRefbyId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const savedLogin = localStorage.getItem('loginData');
    const adminData = savedLogin ? JSON.parse(savedLogin) : null;
    if (!adminData || !Array.isArray(adminData.access) || !adminData.access.includes('1')) navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const email = emailQuery.trim();
    if (email.length < 2) {
      setSearchResults([]);
      setHasSearched(false);
      setError('');
      return undefined;
    }

    let active = true;
    const timeoutId = window.setTimeout(async () => {
      setIsSearching(true);
      setError('');
      try {
        const response = await searchClientsByEmail(email);
        if (active) {
          setSearchResults(getClients(response.data));
          setHasSearched(true);
        }
      } catch (requestError) {
        if (active) {
          setSearchResults([]);
          setHasSearched(true);
          setError(getErrorMessage(requestError, 'Unable to search clients.'));
        }
      } finally {
        if (active) setIsSearching(false);
      }
    }, 350);

    return () => { active = false; window.clearTimeout(timeoutId); };
  }, [emailQuery]);

  const selectClient = (client) => {
    setSelectedClient(client);
    setRefbyId(client.refbyId || '');
    setError('');
  };

  const saveRefById = async (event) => {
    event.preventDefault();
    const value = refbyId.trim();
    if (!selectedClient?._id) return setError('Select a client before saving.');
    if (!value) return setError('RefBy ID is required.');

    setIsSaving(true);
    setError('');
    try {
      const response = await updateClientRefById({ partnerId: selectedClient._id, refbyId: value });
      if (response.data?.success === false) throw new Error(response.data?.message || 'Unable to update RefBy ID.');

      const updatedClient = {
        ...selectedClient,
        ...(response.data?.data && typeof response.data.data === 'object' ? response.data.data : {}),
        refbyId: response.data?.data?.refbyId ?? value,
      };
      setSelectedClient(updatedClient);
      setSearchResults((results) => results.map((client) => client._id === selectedClient._id ? { ...client, refbyId: updatedClient.refbyId } : client));
      toast.success(response.data?.message || 'RefBy ID updated successfully.');
    } catch (requestError) {
      const message = getErrorMessage(requestError, 'Unable to update RefBy ID.');
      setError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return <div className="wrapper"><Navbar /><main className="container mt-5 client-refby-page" id="content"><div className="mb-4"><h1 className="mb-1">Client RefBy ID</h1><p className="text-muted mb-0">Find a franchise or client by email and add or update its RefBy ID.</p></div><section className="card mb-4"><div className="card-body"><ClientEmailSearch value={emailQuery} onChange={setEmailQuery} disabled={isSaving} isSearching={isSearching} />{error && <div className="alert alert-danger mt-3 mb-0" role="alert">{error}</div>}{hasSearched && !isSearching && !error && searchResults.length === 0 && <div className="client-refby-empty">No client found</div>}{searchResults.length > 0 && <ClientSearchResults clients={searchResults} selectedClientId={selectedClient?._id} onSelect={selectClient} disabled={isSaving} />}</div></section>{selectedClient && <section className="card"><div className="card-body"><SelectedClientCard client={selectedClient} /><RefByIdForm refbyId={refbyId} onChange={setRefbyId} onSubmit={saveRefById} isSaving={isSaving} /></div></section>}</main><ToastContainer position="top-right" autoClose={4000} /></div>;
}

function ClientEmailSearch({ value, onChange, disabled, isSearching }) { return <div><label className="form-label" htmlFor="client-email-search">Client email</label><div className="client-refby-search"><input id="client-email-search" type="search" className="form-control" value={value} onChange={(event) => onChange(event.target.value)} placeholder="Enter at least 2 characters of an email" disabled={disabled} /><span className="client-refby-search-status">{isSearching ? 'Searching…' : ''}</span></div><div className="form-text">Search franchise/client using email</div></div>; }

function ClientSearchResults({ clients, selectedClientId, onSelect, disabled }) { return <div className="client-refby-results mt-3" role="listbox" aria-label="Client search results">{clients.map((client) => <button key={client._id} type="button" className={`client-refby-result ${selectedClientId === client._id ? 'selected' : ''}`} onClick={() => onSelect(client)} disabled={disabled}><span className="client-refby-result-title">{client.labName || '-'}</span><span>{client.email || '-'}</span><span>{client.mobile || '-'} · {client.status || '-'}</span><span>Current refbyId: {client.refbyId || 'Not set'}</span><ProcessingCenters centers={client.labids} /></button>)}</div>; }

function SelectedClientCard({ client }) { return <div className="client-refby-selected mb-4"><h2 className="h4 mb-3">Selected client</h2><div className="row g-3"><ClientDetail label="Client" value={client.labName} /><ClientDetail label="Email" value={client.email} /><ClientDetail label="Mobile" value={client.mobile} /><ClientDetail label="Status" value={client.status} /><ClientDetail label="Current refbyId" value={client.refbyId || 'Not set'} /><div className="col-12"><div className="client-refby-label">Linked processing centers</div><ProcessingCenters centers={client.labids} /></div></div></div>; }
function ClientDetail({ label, value }) { return <div className="col-12 col-sm-6 col-lg-4"><div className="client-refby-label">{label}</div><div>{value || '-'}</div></div>; }
function ProcessingCenters({ centers }) { if (!Array.isArray(centers) || centers.length === 0) return <span className="text-muted small">No linked processing centers</span>; return <div className="client-refby-centers">{centers.map((center, index) => <span className="badge text-bg-secondary" key={center?._id || `${center?.labCode}-${index}`}>{center?.labCode || '-'} {center?.labName ? `· ${center.labName}` : ''}</span>)}</div>; }
function RefByIdForm({ refbyId, onChange, onSubmit, isSaving }) { return <form className="border-top pt-4" onSubmit={onSubmit}><label className="form-label" htmlFor="refby-id">RefBy ID</label><input id="refby-id" className="form-control client-refby-input" value={refbyId} onChange={(event) => onChange(event.target.value)} disabled={isSaving} required /><div className="form-text mb-3">This will add or update refbyId for the selected client.</div><button type="submit" className="btn btn-primary" disabled={isSaving}>{isSaving ? 'Saving…' : 'Save RefBy ID'}</button></form>; }

export default ClientRefById;
